import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
} from "@mui/material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Mail,
  MessageCircle,
  BookOpen,
  Send,
  CheckCircle,
  HelpCircle,
  Clock,
  ExternalLink,
} from "lucide-react";
import BRAND from "../theme/talktokam";

const inputSx = {
  mb: 2.5,
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    bgcolor: "rgba(255,255,255,0.05)",
    "& fieldset": { borderColor: "rgba(255,255,255,0.1)" },
    "&:hover fieldset": { borderColor: "rgba(56,189,248,0.4)" },
    "&.Mui-focused fieldset": { borderColor: "#6366f1" },
  },
  "& .MuiInputLabel-root": { color: "#94a3b8" },
  "& .MuiInputBase-input": { color: "#fff" },
  "& .MuiSelect-icon": { color: "#64748b" },
};

const subjects = [
  "Support",
  "Sales Inquiry",
  "Billing",
  "Partnership",
  "Feature Request",
  "Other",
];

const Contact = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [sent, setSent] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.email.trim()) e.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Invalid email address";
    if (!form.subject) e.subject = "Please select a subject";
    if (!form.message.trim()) e.message = "Message is required";
    else if (form.message.trim().length < 10)
      e.message = "Message must be at least 10 characters";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSent(true);
  };

  const handleChange = (field, value) => {
    setForm({ ...form, [field]: value });
    if (errors[field]) setErrors({ ...errors, [field]: "" });
  };

  return (
    <Box sx={{ background: "#050816", minHeight: "100vh" }}>
      <Box
        sx={{
          position: "relative",
          textAlign: "center",
          py: { xs: 10, md: 14 },
          background: `radial-gradient(ellipse 800px 600px at 20% 10%, ${BRAND.cyan}33 0%, transparent 60%), radial-gradient(ellipse 700px 500px at 85% 90%, ${BRAND.purple}33 0%, transparent 60%), radial-gradient(ellipse 600px 400px at 50% 50%, ${BRAND.primary}1a 0%, transparent 60%), linear-gradient(180deg, #050816 0%, #0a0e27 50%, #050816 100%)`,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: `linear-gradient(rgba(56,189,248,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(56,189,248,0.04) 1px, transparent 1px)`,
            backgroundSize: "60px 60px",
            maskImage:
              "radial-gradient(ellipse at center, black 30%, transparent 70%)",
            WebkitMaskImage:
              "radial-gradient(ellipse at center, black 30%, transparent 70%)",
            pointerEvents: "none",
          }}
        />
        <motion.div
          animate={{ y: [0, -40, 0] }}
          transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
          style={{
            position: "absolute",
            top: "5%",
            left: "-5%",
            width: 500,
            height: 500,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${BRAND.cyan}40 0%, transparent 70%)`,
            filter: "blur(60px)",
            pointerEvents: "none",
          }}
        />
        <motion.div
          animate={{ y: [0, 40, 0] }}
          transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
          style={{
            position: "absolute",
            bottom: "-10%",
            right: "-10%",
            width: 600,
            height: 600,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${BRAND.purple}40 0%, transparent 70%)`,
            filter: "blur(80px)",
            pointerEvents: "none",
          }}
        />
        <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: "2rem", md: "3rem" },
                background: "linear-gradient(135deg, #38bdf8, #a855f7)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                mb: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5,
              }}
            >
              {/* <Mail size={32} /> */}
              Let's Talk
            </Typography>
            <Typography
              sx={{
                fontSize: "1.1rem",
                color: "#94a3b8",
                maxWidth: 500,
                mx: "auto",
                mb: 2,
              }}
            >
              Have a question, need a demo, or want to share feedback? We're
              here to help.
            </Typography>
            <Chip
              icon={<Clock size={14} />}
              label="We typically respond within 24 hours"
              size="small"
              sx={{
                bgcolor: "rgba(56,189,248,0.1)",
                color: "#38bdf8",
                fontSize: "0.75rem",
                fontWeight: 500,
                "& .MuiChip-icon": { ml: 0.5 },
              }}
            />
          </motion.div>
        </Container>
      </Box>

      <Container sx={{ py: { xs: 6, md: 10 } }}>
        <Grid container spacing={5} justifyContent="center">
          <Grid item xs={12} md={7}>
            {sent ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
              >
                <Box
                  sx={{
                    textAlign: "center",
                    p: { xs: 4, md: 6 },
                    borderRadius: 4,
                    bgcolor: "rgba(16,185,129,0.08)",
                    border: "1px solid rgba(16,185,129,0.25)",
                  }}
                >
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: "50%",
                      bgcolor: "rgba(16,185,129,0.15)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mx: "auto",
                      mb: 2,
                    }}
                  >
                    <CheckCircle size={32} color="#10b981" />
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "1.4rem",
                      color: "#10b981",
                      mb: 1,
                    }}
                  >
                    Message Sent!
                  </Typography>
                  <Typography
                    sx={{ color: "#94a3b8", mb: 3, maxWidth: 400, mx: "auto" }}
                  >
                    Thank you for reaching out. Our team will get back to you
                    within 24 hours. You'll hear from us at{" "}
                    <strong style={{ color: "#e2e8f0" }}>{form.email}</strong>.
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setSent(false);
                      setForm({
                        name: "",
                        email: "",
                        subject: "",
                        message: "",
                      });
                    }}
                    sx={{
                      borderRadius: "8px",
                      textTransform: "none",
                      borderColor: "rgba(255,255,255,0.2)",
                      color: "#94a3b8",
                      "&:hover": { borderColor: "#fff", color: "#fff" },
                    }}
                  >
                    Send Another Message
                  </Button>
                </Box>
              </motion.div>
            ) : (
              <Box component="form" onSubmit={handleSubmit} noValidate>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: "1.2rem",
                    color: "#fff",
                    mb: 3,
                  }}
                >
                  Send us a message
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  label="Your Name"
                  value={form.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  error={!!errors.name}
                  helperText={errors.name}
                  required
                  sx={inputSx}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Email"
                  type="email"
                  value={form.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  error={!!errors.email}
                  helperText={errors.email}
                  required
                  sx={inputSx}
                />
                <FormControl
                  fullWidth
                  size="small"
                  sx={inputSx}
                  error={!!errors.subject}
                >
                  <InputLabel sx={{ color: "#94a3b8" }}>Subject</InputLabel>
                  <Select
                    value={form.subject}
                    label="Subject"
                    onChange={(e) => handleChange("subject", e.target.value)}
                    sx={{
                      color: "#fff",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(255,255,255,0.1)",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(56,189,248,0.4)",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#6366f1",
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          bgcolor: "rgba(10,14,39,0.96)",
                          backdropFilter: "blur(20px)",
                          border: "1px solid rgba(255,255,255,0.08)",
                          borderRadius: "12px",
                          mt: 0.5,
                          boxShadow: "0 8px 30px rgba(0,0,0,0.3)",
                        },
                      },
                    }}
                  >
                    {subjects.map((s) => (
                      <MenuItem
                        key={s}
                        value={s}
                        sx={{
                          color: "#e2e8f0",
                          "&:hover": { bgcolor: "rgba(56,189,248,0.1)" },
                          "&.Mui-selected": {
                            bgcolor: "rgba(56,189,248,0.15)",
                          },
                        }}
                      >
                        {s}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.subject && (
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        color: "#ef4444",
                        ml: 1.5,
                        mt: 0.5,
                      }}
                    >
                      {errors.subject}
                    </Typography>
                  )}
                </FormControl>
                <TextField
                  fullWidth
                  multiline
                  rows={5}
                  label="Message"
                  value={form.message}
                  onChange={(e) => handleChange("message", e.target.value)}
                  error={!!errors.message}
                  required
                  sx={inputSx}
                  inputProps={{ maxLength: 2000 }}
                  FormHelperTextProps={{
                    sx: {
                      display: "flex",
                      justifyContent: "space-between",
                      mx: 0,
                      color: "#64748b",
                      fontSize: "0.7rem",
                    },
                  }}
                  helperText={
                    (errors.message || "") +
                    (errors.message ? " \u2022 " : "") +
                    `${form.message.length}/2000 characters`
                  }
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  sx={{
                    borderRadius: "10px",
                    textTransform: "none",
                    fontWeight: 700,
                    py: 1.5,
                    background: "linear-gradient(135deg, #4F46E5, #7C3AED)",
                    boxShadow: "0 4px 15px rgba(79,70,229,0.4)",
                    "&:hover": { boxShadow: "0 6px 25px rgba(79,70,229,0.6)" },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                  }}
                >
                  <Send size={18} /> Send Message
                </Button>
              </Box>
            )}
          </Grid>

          <Grid item xs={12} md={5}>
            <Box
              sx={{
                p: 3,
                borderRadius: 4,
                bgcolor: "rgba(255,255,255,0.04)",
                backdropFilter: "blur(16px)",
                border: "1px solid rgba(255,255,255,0.08)",
                height: "100%",
                transition: "all 0.3s",
                "&:hover": {
                  borderColor: "rgba(56,189,248,0.3)",
                  boxShadow: "0 12px 40px rgba(56,189,248,0.1)",
                },
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "1.2rem",
                  color: "#fff",
                  mb: 3,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <HelpCircle size={20} color="#38bdf8" />
                Other ways to reach us
              </Typography>

              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: "rgba(56,189,248,0.06)",
                  border: "1px solid rgba(56,189,248,0.12)",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "8px",
                      bgcolor: "rgba(56,189,248,0.15)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Mail size={16} color="#38bdf8" />
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.9rem",
                      color: "#38bdf8",
                    }}
                  >
                    Email
                  </Typography>
                </Box>
                <Typography
                  sx={{ fontSize: "0.85rem", color: "#cbd5e1", mb: 0.5 }}
                >
                  <a
                    href="mailto:info@talktokam.com"
                    style={{ color: "#cbd5e1", textDecoration: "none" }}
                  >
                    info@talktokam.com
                  </a>
                </Typography>
                <Typography sx={{ fontSize: "0.85rem", color: "#cbd5e1" }}>
                  <a
                    href="mailto:support@talktokam.com"
                    style={{ color: "#cbd5e1", textDecoration: "none" }}
                  >
                    support@talktokam.com
                  </a>
                </Typography>
              </Box>

              {/*
              <Box sx={{ mb: 3, p: 2, borderRadius: 2, bgcolor: "rgba(139,92,246,0.06)", border: "1px solid rgba(139,92,246,0.12)" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                  <Box sx={{ width: 32, height: 32, borderRadius: "8px", bgcolor: "rgba(139,92,246,0.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <Phone size={16} color="#8b5cf6" />
                  </Box>
                  <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", color: "#8b5cf6" }}>Phone</Typography>
                </Box>
                <Typography sx={{ fontSize: "0.85rem", color: "#cbd5e1" }}>+91 XXXXX XXXXX</Typography>
              </Box>

              <Box sx={{ mb: 3, p: 2, borderRadius: 2, bgcolor: "rgba(245,158,11,0.06)", border: "1px solid rgba(245,158,11,0.12)" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                  <Box sx={{ width: 32, height: 32, borderRadius: "8px", bgcolor: "rgba(245,158,11,0.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <MapPin size={16} color="#f59e0b" />
                  </Box>
                  <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", color: "#f59e0b" }}>Office</Typography>
                </Box>
                <Typography sx={{ fontSize: "0.85rem", color: "#cbd5e1" }}>Address coming soon</Typography>
              </Box>
              */}

              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: "rgba(16,185,129,0.06)",
                  border: "1px solid rgba(16,185,129,0.12)",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "8px",
                      bgcolor: "rgba(16,185,129,0.15)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <MessageCircle size={16} color="#10b981" />
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.9rem",
                      color: "#10b981",
                    }}
                  >
                    Live Chat
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: "0.85rem", color: "#94a3b8" }}>
                  Available on website — Mon-Fri, 9AM-6PM IST
                </Typography>
              </Box>

              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: "rgba(56,189,248,0.06)",
                  border: "1px solid rgba(56,189,248,0.12)",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "8px",
                      bgcolor: "rgba(56,189,248,0.15)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <ExternalLink size={16} color="#38bdf8" />
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.9rem",
                      color: "#38bdf8",
                    }}
                  >
                    Twitter / X
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: "0.85rem", color: "#94a3b8" }}>
                  @talktokam
                </Typography>
              </Box>

              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: "rgba(139,92,246,0.06)",
                  border: "1px solid rgba(139,92,246,0.12)",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "8px",
                      bgcolor: "rgba(139,92,246,0.15)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <BookOpen size={16} color="#8b5cf6" />
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.9rem",
                      color: "#8b5cf6",
                    }}
                  >
                    Knowledge Base
                  </Typography>
                </Box>
                <Typography
                  sx={{ fontSize: "0.85rem", color: "#94a3b8", mb: 1.5 }}
                >
                  Find instant answers in our help center.
                </Typography>
                <Button
                  fullWidth
                  variant="outlined"
                  size="small"
                  onClick={() => navigate("/help-center")}
                  sx={{
                    borderRadius: "8px",
                    textTransform: "none",
                    fontSize: "0.8rem",
                    borderColor: "rgba(139,92,246,0.3)",
                    color: "#8b5cf6",
                    "&:hover": {
                      bgcolor: "rgba(139,92,246,0.1)",
                      borderColor: "#8b5cf6",
                    },
                  }}
                >
                  Visit Help Center
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Box
          sx={{
            mt: 6,
            textAlign: "center",
            p: 4,
            borderRadius: 4,
            bgcolor: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(255,255,255,0.06)",
            transition: "all 0.3s",
            "&:hover": { borderColor: "rgba(56,189,248,0.2)" },
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "0.95rem",
              color: "#e2e8f0",
              mb: 0.5,
            }}
          >
            <CheckCircle
              size={16}
              style={{
                marginRight: 6,
                verticalAlign: "middle",
                color: "#10b981",
              }}
            />
            We aim to respond to all inquiries within{" "}
            <strong style={{ color: "#38bdf8" }}>24 hours</strong>
          </Typography>
          <Typography sx={{ fontSize: "0.85rem", color: "#64748b" }}>
            For urgent issues, email{" "}
            <a href="mailto:support@talktokam.com" style={{ color: "#38bdf8" }}>
              support@talktokam.com
            </a>{" "}
            with "URGENT" in the subject line.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Contact;
